import { useState, useRef, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore, updateUser, getAvatarUrl } from '@/hooks/useAppStore';
import { uploadAvatar, updateMe } from '@/api/user';

export default function ProfileEditPage() {
  const navigate = useNavigate();
  const { user } = useAppStore();
  const { t } = useTranslation();

  const [nickname, setNickname] = useState(user?.nickname ?? '');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [uploadedAvatarUrl, setUploadedAvatarUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentAvatar = avatarPreview ?? getAvatarUrl(user);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const handleAvatarSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show local preview immediately
    const localUrl = URL.createObjectURL(file);
    setAvatarPreview(localUrl);

    setIsUploading(true);
    try {
      const result = await uploadAvatar(file);
      setUploadedAvatarUrl(result.url);
      setAvatarPreview(result.url);
    } catch {
      showToast(t('profile_edit_avatarUploadFail'));
      setAvatarPreview(null);
      setUploadedAvatarUrl(null);
    } finally {
      setIsUploading(false);
      // Reset file input so same file can be re-selected if needed
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const nicknameIsValid = nickname.trim().length >= 1 && nickname.trim().length <= 20;
  const canSave = nicknameIsValid && !isUploading && !isSaving;

  const handleSave = async () => {
    if (!canSave) return;

    setIsSaving(true);
    try {
      const payload: { nickname?: string; avatar?: string } = {
        nickname: nickname.trim(),
      };
      if (uploadedAvatarUrl) {
        payload.avatar = uploadedAvatarUrl;
      }
      await updateMe(payload);
      updateUser({
        nickname: nickname.trim(),
        ...(uploadedAvatarUrl ? { avatar: uploadedAvatarUrl } : {}),
      });
      showToast(t('profile_edit_saveSuccess'));
      setTimeout(() => navigate(-1), 1000);
    } catch {
      showToast(t('profile_edit_saveFail'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen font-nunito flex flex-col"
      style={{ background: 'linear-gradient(160deg, #FFF0F6 0%, #F5EEFF 50%, #F0F4FF 100%)', paddingBottom: 'env(safe-area-inset-bottom)' }}>

      {/* 背景装饰球 */}
      <div className="absolute top-0 right-0 w-56 h-56 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, #FF6B9D22 0%, transparent 70%)', transform: 'translate(20%, -20%)' }} />
      <div className="absolute top-32 left-0 w-40 h-40 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, #A855F718 0%, transparent 70%)', transform: 'translate(-30%, 0)' }} />

      {/* 顶部导航 */}
      <div className="relative z-10 flex items-center gap-3 px-5 pb-4" style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top))' }}>
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 flex items-center justify-center rounded-2xl cursor-pointer transition-all active:scale-95"
          style={{ background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)' }}
        >
          <i className="ri-arrow-left-line text-gray-700 text-lg"></i>
        </button>
        <h1 className="text-lg font-bold text-gray-800">{t('profile_edit_title')}</h1>
      </div>

      {/* 主内容 */}
      <div className="relative z-10 flex-1 px-5 pt-4 space-y-5" style={{ paddingBottom: 'calc(2.5rem + env(safe-area-inset-bottom))' }}>

        {/* 头像区域 */}
        <div className="bg-white/85 backdrop-blur rounded-3xl p-6 flex flex-col items-center"
          style={{ border: '1px solid rgba(255,107,157,0.12)' }}>
          <p className="text-xs font-semibold text-gray-400 mb-4 self-start">{t('profile_edit_avatar')}</p>
          <div className="relative mb-3">
            <div className="w-28 h-28 rounded-full overflow-hidden"
              style={{ outline: '4px solid rgba(255,107,157,0.3)' }}>
              <img
                data-testid="avatar-preview-img"
                src={currentAvatar}
                alt={t('profile_edit_avatar')}
                fetchPriority="high"
                className="w-full h-full object-cover"
              />
            </div>
            {isUploading && (
              <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                <i className="ri-loader-4-line text-white text-2xl animate-spin"></i>
              </div>
            )}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full flex items-center justify-center shadow-md cursor-pointer disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
            >
              <i className="ri-camera-line text-white text-sm"></i>
            </button>
          </div>
          <p className="text-xs text-gray-400">{t('profile_edit_tapToChange')}</p>
          <input
            ref={fileInputRef}
            data-testid="avatar-file-input"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleAvatarSelect}
          />
        </div>

        {/* 昵称区域 */}
        <div className="bg-white/85 backdrop-blur rounded-3xl p-5"
          style={{ border: '1px solid rgba(255,107,157,0.12)' }}>
          <label className="block text-xs font-semibold text-gray-400 mb-3">{t('profile_edit_nickname')}</label>
          <input
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            maxLength={20}
            placeholder={t('profile_edit_nicknamePlaceholder')}
            className="w-full bg-transparent text-gray-800 text-sm font-medium outline-none placeholder-gray-300"
          />
          <div className="h-px mt-3" style={{ background: 'linear-gradient(90deg, transparent, #FF6B9D30, #A855F730, transparent)' }} />
          <p className="text-right text-xs text-gray-300 mt-2">{nickname.length}/20</p>
        </div>

        {/* 保存按钮 */}
        <button
          onClick={handleSave}
          disabled={!canSave}
          className="w-full h-14 rounded-2xl text-white font-bold text-base cursor-pointer whitespace-nowrap disabled:opacity-50 transition-opacity"
          style={{ background: 'linear-gradient(135deg, #FF6B9D 0%, #A855F7 100%)' }}
        >
          {isSaving ? t('profile_edit_saving') : t('profile_edit_save')}
        </button>
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-24 left-1/2 z-50 -translate-x-1/2 px-4 py-2.5 rounded-2xl text-sm font-semibold text-white shadow-lg animate-slide-up"
          style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)', maxWidth: '80vw', textAlign: 'center' }}>
          {toast}
        </div>
      )}
    </div>
  );
}
